import React from 'react';
import { type IntlShape, defineMessages } from 'react-intl';

import DefaultExtensionProvider from '@atlaskit/editor-common/extensions/default-extension-provider';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type {
	ContextualToolbar,
	ToolbarItem,
} from '@atlaskit/editor-common/extensions/extension-manifest-toolbar-item';
import type { Parameters } from '@atlaskit/editor-common/extensions/extension-parameters';
import AKIcon from '@atlaskit/icon/core/department';

import { getMacroMetaData } from '@confluence/fabric-extension-lib/entry-points/editor-extensions.ts';
import { EXTENSION_TYPE } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';

import { fireAnalyticsEventForConnectionsButton } from './toolbar-analytics.ts';

export const i18n: {
	toolbarItemLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	toolbarItemLabel: {
		id: 'app-referentiality.provider.toolbar.item.label',
		defaultMessage: 'Manage connected data',
		description:
			"The text for tooltip and label for 'Manage Connections' button in floating toolbar",
	},
});

export const getConnectionsToolbarItem = ({
	intl,
	openConnectionsPanel,
	fireAnalytics,
}: {
	intl: any;
	openConnectionsPanel: any;
	fireAnalytics: any;
}): ToolbarItem => ({
	key: 'connections',
	label: intl.formatMessage(i18n.toolbarItemLabel),
	display: 'icon' as const,
	tooltip: intl.formatMessage(i18n.toolbarItemLabel),
	icon: () => {
		return (() => (
			<AKIcon spacing="spacious" label={intl.formatMessage(i18n.toolbarItemLabel)} />
		)) as any;
	},
	action: async (adf) => {
		const localId: string = adf.attrs?.localId;

		if (localId) {
			openConnectionsPanel(localId);
			fireAnalytics(adf);
		}
	},
});

const getTableContextualToolbar = ({
	intl,
	openConnectionsPanel,
	createAnalyticsEvent,
}): ContextualToolbar => ({
	context: {
		type: 'node',
		nodeType: 'table',
	},
	toolbarItems: [
		getConnectionsToolbarItem({
			intl,
			openConnectionsPanel,
			fireAnalytics: (adf) => fireAnalyticsEventForConnectionsButton(createAnalyticsEvent, adf),
		}),
	],
});

const getConnectContextualToolbars = async ({
	intl,
	openConnectionsPanel,
	createAnalyticsEvent,
}): Promise<ContextualToolbar[]> => {
	const allMacros = await getMacroMetaData();

	// All macros support referentiality
	const allMacrosWithRefDataSchema = allMacros.filter(
		(macro) =>
			macro.pluginKey === 'com.atlassian.plugins.atlassian-connect-plugin' &&
			(macro.refDataSchema?.inputType || macro.refDataSchema?.outputType),
	);

	return allMacrosWithRefDataSchema.reduce((acc, macro) => {
		const context = {
			type: 'extension' as const,
			nodeType: 'extension' as const,
			extensionKey: macro.macroName,
			extensionType: EXTENSION_TYPE.MACRO_CORE,
		};

		return [
			...acc,
			{
				context,
				toolbarItems: [
					getConnectionsToolbarItem({
						intl,
						openConnectionsPanel,
						fireAnalytics: (adf) =>
							fireAnalyticsEventForConnectionsButton(createAnalyticsEvent, adf),
					}),
				],
			},
		];
	}, [] as ContextualToolbar[]);
};

type ReferentialityExtensionProvider = {
	intl: IntlShape;
	openConnectionsPanel?: (localId: string) => void;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
};

export const referentialityExtensionProvider = async ({
	intl,
	openConnectionsPanel = () => null,
	createAnalyticsEvent,
}: ReferentialityExtensionProvider): Promise<DefaultExtensionProvider<Parameters>> => {
	return new DefaultExtensionProvider([
		{
			title: 'Referentiality',
			type: 'com.atlassian.confluence.referentiality',
			key: 'referentiality',
			icons: {
				'48': () =>
					import(
						/* webpackChunkName: "loadable-atlaskiticonglyphaddon" */ '@atlaskit/icon/core/app'
					),
			},
			modules: {
				contextualToolbars: [
					getTableContextualToolbar({
						intl,
						openConnectionsPanel,
						createAnalyticsEvent,
					}),
					...(await getConnectContextualToolbars({
						intl,
						openConnectionsPanel,
						createAnalyticsEvent,
					})),
				],
			},
		},
	]);
};
